import baseApi from "../api/baseApi.ts";
import type {
    WatchlistAddRequest,
    WatchlistAddResponse,
    WatchlistEntryTypesResponse,
    WatchlistListParams,
    WatchlistListResponse,
    WatchlistRemoveResponse,
    WatchlistToggleRequest,
    WatchlistToggleResponse
} from "../api/types.ts";

// Extend the base API with watchlist-specific endpoints
export const watchlistApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        // POST /watchlist/add/ - Add item to watchlist
        addWatchlistItem: builder.mutation<WatchlistAddResponse, WatchlistAddRequest>({
            query: (data) => ({
                url: '/watchlist/add/',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [{ type: 'Watchlist', id: 'LIST' }],
        }),

        // GET /watchlist/entry-types/ - Get supported entry types
        getWatchlistEntryTypes: builder.query<WatchlistEntryTypesResponse, void>({
            query: () => ({
                url: '/watchlist/entry-types/',
                method: 'GET',
            }),
            providesTags: [{ type: 'Watchlist', id: 'ENTRY_TYPES' }],
        }),

        // GET /watchlist/list/ - List watchlist items with optional filtering
        getWatchlistItems: builder.query<WatchlistListResponse, WatchlistListParams | void>({
            query: (params = {}) => ({
                url: '/watchlist/list/',
                method: 'GET',
                params,
            }),
            providesTags: (result) =>
                result
                    ? [
                        ...result.results.map(({ id }) => ({ type: 'Watchlist' as const, id })),
                        { type: 'Watchlist', id: 'LIST' },
                    ]
                    : [{ type: 'Watchlist', id: 'LIST' }],
        }),

        // DELETE /watchlist/remove/{item_id}/ - Remove item from watchlist
        removeWatchlistItem: builder.mutation<WatchlistRemoveResponse, string | number>({
            query: (itemId) => ({
                url: `/watchlist/remove/${itemId}/`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, itemId) => [
                { type: 'Watchlist', id: itemId },
                { type: 'Watchlist', id: 'LIST' },
            ],
        }),

        // PATCH /watchlist/toggle/{item_id}/ - Enable or disable watchlist item
        toggleWatchlistItem: builder.mutation<
            WatchlistToggleResponse,
            { itemId: string | number; data: WatchlistToggleRequest }
        >({
            query: ({ itemId, data }) => ({
                url: `/watchlist/toggle/${itemId}/`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { itemId }) => [
                { type: 'Watchlist', id: itemId },
                { type: 'Watchlist', id: 'LIST' },
            ],
        }),
    }),
    overrideExisting: false,
});

// Export hooks for usage in components
export const {
    useAddWatchlistItemMutation,
    useGetWatchlistEntryTypesQuery,
    useGetWatchlistItemsQuery,
    useLazyGetWatchlistItemsQuery,
    useRemoveWatchlistItemMutation,
    useToggleWatchlistItemMutation,
} = watchlistApi;